/**
 * This file is part of vue-boilerplate.
 * @link     : https://zhaiyiming.com/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 TINYMINS.
 */

import get from 'lodash/get';
import store from '@/store';
import * as api from '@/api/user';
import { UserFull, WebrtcOptions } from '@/api/types/user';
import { USER, CHAT } from '@/store/types';
import { HttpResponseData } from '@/api/driver/http';
import { finalizeAction, ActionType, StoreActionEnv } from '@/store/actions';
import router from '@/router';
import { AUTH_STATE } from '@/config';
import { updateObject } from '@/utils/util';
import { camelize } from '@/utils/transfer';
import { checkAuthorizeRedirect } from '@/utils/authorization';
import { isRun, isInDevMode, isTimEnabled } from '@/utils/environment';
import { createPayment } from '@/utils/create-payment';
import profileModule, { StoreUserProfileState } from './profile';
import configModule, { StoreUserConfigState } from './config';
import accessModule, { StoreUserAccessState } from './access';
import blockModule, { StoreUserBlockState } from './block';
import otherModule, { StoreUserOtherState } from './other';
import followModule, { StoreUserFollowState } from './follow';
import findModule, { StoreUserFindState } from './find';
import matchesModule, { StoreUserMatchesState } from './matches';
import adminModule, { StoreUserAdminState } from './admin';
import coupleModule, { StoreUserCoupleState } from './couple';
import vipModule, { StoreUserVipState } from './vip';
import verifyModule, { StoreUserVerifyState } from './verify';
import imModule, { StoreUserImState } from './im';
import coinMode, { StoreUserCoinState } from './coin';
import atMode, { StoreUserAtState } from './at';
import codeModule, { StoreUserCodeState } from './code';
import gameModule, { StoreUserGameState } from './game';
import onlineModule, { StoreUserOnlineState } from './online';
import medalsModule, { StoreUserMedalsState } from './medals';
import fieldModule, { StoreUserFieldState } from './field';
import accountModule, { StoreUserAccountState } from './account';
import tonesModule, { StoreUserTonesState } from './tones';
import messageModule, { StoreUserMessageState } from './message';

export interface StoreUserState {
  codeTime: number;
  user: UserFull;
  errmsg: string;
  prevUser: UserFull;
  status: number;
  referral: number; // 介绍人id
  point: string;
  limit: number;
  onlineUser: UserFull[];
  noMore: boolean;
  lock: boolean;
  webrtc: WebrtcOptions;
  // modules
  profile: StoreUserProfileState;
  config: StoreUserConfigState;
  access: StoreUserAccessState;
  block: StoreUserBlockState;
  other: StoreUserOtherState;
  follow: StoreUserFollowState;
  find: StoreUserFindState;
  matches: StoreUserMatchesState;
  admin: StoreUserAdminState;
  couple: StoreUserCoupleState;
  vip: StoreUserVipState;
  verify: StoreUserVerifyState;
  im: StoreUserImState;
  coin: StoreUserCoinState;
  at: StoreUserAtState;
  code: StoreUserCodeState;
  game: StoreUserGameState;
  online: StoreUserOnlineState;
  medals: StoreUserMedalsState;
  field: StoreUserFieldState;
  account: StoreUserAccountState;
  tones: StoreUserTonesState;
  message: StoreUserMessageState;
}

export interface StoreUserGetters {
  user?: UserFull;
}

export default {
  namespaced: true,
  modules: {
    profile: profileModule,
    config: configModule,
    access: accessModule,
    block: blockModule,
    other: otherModule,
    follow: followModule,
    find: findModule,
    matches: matchesModule,
    admin: adminModule,
    couple: coupleModule,
    vip: vipModule,
    verify: verifyModule,
    im: imModule,
    coin: coinMode,
    at: atMode,
    code: codeModule,
    game: gameModule,
    online: onlineModule,
    medals: medalsModule,
    field: fieldModule,
    account: accountModule,
    tones: tonesModule,
    message: messageModule,
  },
  state: {
    codeTime: 0,
    user: null,
    errmsg: null,
    prevUser: null,
    status: null,
    referral: 0,
    point: '',
    limit: 20,
    onlineUser: [],
    noMore: false,
    lock: false,
    webrtc: null,
  },
  getters: {
    user: (state) => {
      if (state.status === AUTH_STATE.LOGGED_IN && state.user && Object.keys(state.user).length !== 0) {
        return state.user;
      }
      return null;
    },
    status: state => state.status,
  },
  actions: {
    [USER.GET_PHONE_CODE]({ commit }, { phone, captcha, scene, errcodeExpected }) {
      return new Promise((resolve, reject) => {
        api.getPhoneCode(phone, captcha, scene, errcodeExpected).then((res) => {
          commit(USER.GET_PHONE_CODE, { leftTime: res.data.leftTime });
          resolve(res);
        }).catch((err) => {
          commit(USER.GET_PHONE_CODE, { leftTime: get(err.response, 'data.leftTime', -1) });
          reject(err);
        });
      });
    },
    [USER.LOGIN]({ dispatch, rootState }, { phone, code }) {
      return new Promise<void>((resolve, reject) => {
        api.login(phone, code).then(() => {
          dispatch(USER.GET, { action: 'reload', silent: true }).then(() => {
            const redirect = rootState.route.query.redirect;
            if (redirect) {
              router.push({ path: redirect });
            } else {
              router.push({ name: 'index' });
            }
            resolve();
          });
        }).catch(reject);
      });
    },
    [USER.LOGOUT]({ commit, rootState }) {
      return new Promise<void>((resolve, reject) => {
        api.logout().then(async () => {
          commit(USER.LOGOUT);
          const { route } = router.resolve(rootState.common.route.to.fullPath);
          const redirect = await checkAuthorizeRedirect(route);
          if (redirect) {
            router.push(redirect);
          }
          resolve();
        }).catch(reject);
      });
    },
    [USER.GET](
      { commit, state }: StoreActionEnv<StoreUserState>,
      { action: rawAction = '', strict = true, silent = false }: {
        action?: ActionType;
        strict?: boolean;
        silent?: boolean;
      } = {},
    ): Promise<void> {
      const action = finalizeAction(rawAction, state.status !== null);
      if (action) {
        // window.__INITIAL_STATE__ = {"errcode":401,"errmsg":"未授权"}; // 测试数据
        if (typeof window.__INITIAL_STATE__ === 'object') {
          const res = camelize<HttpResponseData<UserFull>>(window.__INITIAL_STATE__);
          commit(USER.GET, {
            status: res.errcode,
            user: res.data || {},
            errmsg: res.errmsg,
          });
          delete window.__INITIAL_STATE__;
        } else {
          return new Promise((resolve, reject) => {
            api.getUser(strict, silent).then((res) => {
              commit(USER.GET, {
                status: res.data ? res.errcode : AUTH_STATE.GUEST,
                user: res.data || {},
                errmsg: res.errmsg,
              });
              resolve();
            }).catch((err) => {
              if (err && err.response) {
                commit(USER.GET, {
                  status: err.response.errcode,
                  user: err.response.data || {},
                  errmsg: err.response.errmsg,
                });
                resolve();
              } else {
                reject(err);
              }
            });
          });
        }
      }
      return Promise.resolve();
    },
    [USER.SET_PHONE]({ dispatch }, { phone, code, oldPhone, errcodeExpected }) {
      return new Promise((resolve, reject) => {
        api.setPhone({
          body: {
            phone,
            code,
            oldPhone,
          },
          errcodeExpected,
        }).then((res) => {
          dispatch(USER.GET, { action: 'refresh' }).then(() => resolve(res));
        }).catch(reject);
      });
    },
    [USER.CHECK_REAL_NAME](_, name) {
      return new Promise((resolve, reject) => {
        api.checkRealName(name).then((res) => {
          resolve(res);
        }).catch(reject);
      });
    },
    [USER.GET_WEBRTC_OPTIONS](
      { commit, state }: StoreActionEnv<StoreUserState>,
      { reload = false }: { reload?: boolean } = {},
    ) {
      if (state.webrtc !== null && !reload) {
        const now = Math.floor(Date.now() / 1000);
        if (state.webrtc.paidExpiresAt === 0 || state.webrtc.paidExpiresAt > now) {
          return Promise.resolve();
        }
      }
      return new Promise<void>((resolve, reject) => {
        api.getWebrtcOptions().then((res) => {
          commit(USER.GET_WEBRTC_OPTIONS, res.data);
          resolve();
        }).catch(reject);
      });
    },
    [USER.CREATE_WEBRTC_TOKEN]({ dispatch }: StoreActionEnv<StoreUserState>, body) {
      return new Promise((resolve, reject) => {
        createPayment(() => api.createWebrtcToken(body, { errcodeExpected: [445] })).then((res_) => {
          const res = res_ as HttpResponseData;
          const url = res.data.url;
          dispatch(USER.GET_WEBRTC_OPTIONS, { reload: true }).finally(() => resolve(url));
        }).catch(reject);
      });
    },
  },
  mutations: {
    [USER.GET](state, { status, user, errmsg }: { status: number; user: UserFull; errmsg: string }) {
      if (isTimEnabled() && status === AUTH_STATE.LOGGED_IN && !isRun() && !isInDevMode('manually-chat')) {
        setTimeout(() => {
          store.dispatch(`chat/conversation/list/${CHAT.GET_CONVERSATION_LIST}`, { silent: true });
        }, 5000);
      }
      if (state.prevUser && state.prevUser.id && state.prevUser.id !== user.id) {
        setTimeout(() => window.location.reload(), 1000);
      }
      state.user = user;
      state.errmsg = errmsg;
      state.status = status;
      state.prevUser = user;
    },
    [USER.LOGOUT](state) {
      state.user = {};
      state.status = AUTH_STATE.GUEST;
    },
    [USER.GET_PHONE_CODE](state, { leftTime = 0 }) {
      state.codeTime = Math.floor(new Date().valueOf() / 1000) + leftTime;
    },
    [USER.UPDATE](state, updateInfo) {
      updateObject(state.user, {
        assign: updateInfo.assign,
        offset: updateInfo.offset,
      });
    },
    [USER.GET_WEBRTC_OPTIONS](state, data) {
      state.webrtc = data;
    },
  },
};
